import { HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { isString, map, orderBy } from 'lodash'
import moment from 'moment'
import { Observable, of, throwError } from 'rxjs'
import { catchError, map as oMap, tap } from 'rxjs/operators'

import { PatientIntakeFormData } from '../classes'
import { NOT_FOUND } from '../data'
import { InvoicesStats } from '../data/invoices-stats'
import { PDFDownloadLink } from '../data/pdf-download-link'
import { PatientInsuranceHistoryData, PatientInsuranceHistoryModel } from '../model'
import { AppointmentData, AppointmentHistoryData, AppointmentModel } from '../model/appointment'
import { AppointmentRequest, AppointmentRequestData } from '../model/appointment-request'
import { Card, CardData } from '../model/card'
import { ChargeDescriptionMaster, ChargeDescriptionMasterData } from '../model/cdm'
import { Clinic } from '../model/clinic'
import { Consent, ConsentData } from '../model/consent'
import { Eligibility, EligibilityData } from '../model/eligibility'
import { EstimateTemplateModel } from '../model/estimate'
import { EstimateCharge } from '../model/estimate/estimate-charge'
import { ModelClaimSearchTrace } from '../model/estimate/model-claim-search-trace'
import { EventRecord, EventRecordData } from '../model/event'
import { Facility, FacilityData } from '../model/facility'
import { InsuranceProvider } from '../model/insurance-provider'
import { IntakeData, IntakeModel } from '../model/intake'
import { InvoiceData, InvoiceModel } from '../model/invoice'
import { MessageHistoryRecordData, MessageHistoryRecordModel } from '../model/message-history'
import { MessageTemplate, MessageTemplateData, MessageTemplateModel } from '../model/message-template'
import { Note, NoteData } from '../model/note'
import { Notification, NotificationData, NotificationModel } from '../model/notification'
import { CharityCareTrace, Patient, PatientData, PatientDocument, PatientFileUploadInput, PatientFileUploadPreparation, PatientUploadedFile } from '../model/patient'
import { Procedure, ProcedureData, ProceduresEstimate, ProceduresEstimateItem, ProceduresEstimateResponse, type ProcedureSuggestion, type ProcedureSuggestionResults } from '../model/procedure'
import { PriceModel, ProductCategory, ProductCategoryData, ProductData, ProductModel } from '../model/product'
import { IQuestion, ISection, ISectionPDFDocumentValue } from '../model/question'
import { Role, RoleData } from '../model/role'
import { TransactionData, TransactionModel } from '../model/transaction'
import { Workflow } from '../model/workflow'
import {
  AppointmentEstimateSchema,
} from '../schema'
import { standardizePhone } from '../utils'
import { AuthenticatedRestService } from './auth-rest.service'
import { PagedParams, PagedRestResponse, PatientPalRestError, type RestResponse, toData } from './rest'

export type CsvDelimiter = ',' | 'tab' | ';'
export type CsvQuote = '"' | "'"

export interface DeleteProductCptResult {
  removedProductId: string
  /** The row was the appointment's last procedure, so the appointment was cancelled. */
  appointmentCancelled: boolean
}

/** Result of adding CPT codes to an appointment as new procedure rows. */
/** A picked procedure the add left off whole because some of its codes were already on the appointment. */
export interface SkippedProcedure {
  id: string
  name: string
  codes: string[]
  overlappingCodes: string[]
}

export interface AddProductCptResult {
  newProducts: ProductModel[]
  skippedCodes: string[]
  skippedProducts: SkippedProcedure[]
  /** The ids in `newProducts` the request had to create in the clinic's procedures, rather than reuse. */
  createdProductIds: string[]
}

export interface UpdateProductCptResult {
  oldProductId: string
  newProduct: ProductModel
  /** Whether the route had to create the procedure, rather than reuse one the clinic has. */
  created: boolean
}

export interface PaymentResponse {
  invoice: InvoiceData
  transaction: TransactionData
}

export interface RefundResponse {
  id: string
  status?: boolean
}

export interface CaptureResponse {
  id: string
  status?: boolean
}

export interface IntakeResponse {
  id: string
  patientId: string
  appointmentId: string
}

export interface ListUsersResponse {
  has_more: boolean
  items: ListUser[]
  offset?: number | 0
  limit?: number | 0
  total?: number | 0
}

export interface ListUser {
  id: string
  name: string
  email: string
  account_id: string
  role: string
  last_login_at?: Date
  last_request_at?: Date
  created_at: Date
  updated_at: Date
}

export interface GetPatientsInput extends PagedParams {
  familyName?: string
  givenName?: string
  eid?: string
  dob?: string | Date | moment.Moment
  phone?: string
  email?: string
  lastEvaluatedId?: string
}

export interface GetAppointmentRequestsInput extends PagedParams {
  requestedDate?: string | Date | moment.Moment
  status?: string | string[]
  locationId?: string
}

export interface GetInventoryInput extends PagedParams {
  categories?: string | string[]
  carriers?: string | string[]
  date?: string | Date | moment.Moment
  locationId?: string
  locations?: string | string[]
  providers?: string | string[]
}

export interface GetInventoryStatusDetailsInput extends GetInventoryInput {
  module?: string
  status?: string
  completion?: 'complete' | 'incomplete'
  from?: number | string
  to?: number | string
  startAt?: string
}

export interface GetInvoicesInput extends PagedParams {
  paid?: boolean
  createdAt?: string | Date | moment.Moment
  patientId?: string
  appointmentIds?: string[]
  fromDate?: string | Date | moment.Moment
  toDate?: string | Date | moment.Moment
  status?: string | string[]
  extend?: string[]
}

export interface GetTransactionsInput extends PagedParams {
  paid?: boolean
  createdAt?: string | Date | moment.Moment
  patientId?: string
  fromDate?: string | Date | moment.Moment
  toDate?: string | Date | moment.Moment
  status?: string | string[]
}

export interface GetAppointmentsInput extends PagedParams {
  patientId?: string
  episodeId?: string
  start?: string | Date | moment.Moment
  end?: string | Date | moment.Moment
  status?: string
  isStar?: string
  extend?: string
  locationId?: string
  categoryId?: string
  vipPatients?: string
  project?: string
  waitList?: boolean
  hideCompletedAppointments?: boolean
  hidePastAppointments?: boolean
  productCategories?: string[]
}

export interface ContactSupportFileData {
  fileName: string;
  content: string | ArrayBuffer;
}

export interface ContactSupportParams {
  message: string;
  attachment?: ContactSupportFileData;
}

export interface ModelClaimSearchResult {
  charges: EstimateCharge[]
  trace?: ModelClaimSearchTrace
}

export interface AvailableTimesRequestParams {
  start: string
  end: string
  appointmentRequest: string
}

export type SendPatientMessageMethod = 'email' | 'sms'

export interface AppointmentMutationOptions {
  /**
   * The ID of an Eligibility (typically just-pulled via IV) to link to the
   * appointment. The API uses this to create or update a PatientInsurance
   * row from the eligibility and cache it onto the appointment.
   */
  eligibilityId?: string
}

@Injectable({
  providedIn: 'root',
})
export class ApiService extends AuthenticatedRestService {
  private cache: {
    eligibilities: { [key: string]: Eligibility }
    intakes: { [key: string]: IntakeModel }
    patients: { [key: string]: Patient }
    productCategories: ProductCategory[] | null
    payers?: InsuranceProvider[]
  } = {
      eligibilities: {},
      intakes: {},
      patients: {},
      productCategories: null,
    }

  getIntegrationSupport() {
    const route = `/integration/support`
    return this.get<any>(route)
  }

  getLocalities(): Observable<any[]> {
    return this.get<any[]>('/cms/localities').pipe(
      oMap((response) => response.data),
    )
  }

  getPayers(): Observable<InsuranceProvider[]> {
    if (this.cache.payers) {
      return of(this.cache.payers)
    }
    const route = `/insurances/payers`
    return this.get<InsuranceProvider[]>(route).pipe(
      oMap((response) => orderBy(
        response.data.map((carrier) => new InsuranceProvider(carrier)),
        'name',
      )),
      tap((payers) => this.cache.payers = payers),
    )
  }

  search(criteria: GetPatientsInput): Observable<PagedRestResponse<Patient[]>> {
    return this.getPatients(criteria)
  }

  createAppointment(appointment: AppointmentModel, options?: AppointmentMutationOptions): Observable<AppointmentModel> {
    const route = `/appointments`
    return this.post<AppointmentData>(route, { ...appointment.toJSON(), ...options }).pipe(
      oMap((response) => {
        return new AppointmentModel(response.data)
      }),
    )
  }

  updateAppointment(appointment: AppointmentModel, options?: AppointmentMutationOptions): Observable<AppointmentModel> {
    const route = `/appointments/${appointment.id}`
    return this.put<AppointmentData>(route, { ...appointment.toJSON(), ...options }).pipe(
      oMap((response) => {
        return new AppointmentModel(response.data)
      }),
    )
  }

  updateAppointmentProductCpt(
    appointmentId: string,
    productId: string,
    cptCodes: string[],
  ): Observable<UpdateProductCptResult> {
    const route = `/appointments/${appointmentId}/products/${productId}/cpt`
    return this.put<{ oldProductId: string; newProduct: ProductData; created?: boolean }>(route, { cptCodes }).pipe(
      oMap((response) => {
        return {
          oldProductId: response.data.oldProductId,
          newProduct: new ProductModel(response.data.newProduct),
          created: response.data.created === true,
        }
      }),
    )
  }

  addAppointmentProductCpt(
    appointmentId: string,
    cptCodes: string[],
    studyEid?: string,
    productIds: string[] = [],
  ): Observable<AddProductCptResult> {
    const route = `/appointments/${appointmentId}/products/cpt`
    const body: { cptCodes?: string[]; productIds?: string[]; studyEid?: string } = {}

    // A request carrying only picked procedures leaves the codes out, since the route rejects an empty list.
    if (cptCodes.length > 0 || productIds.length === 0) {
      body.cptCodes = cptCodes
    }

    if (productIds.length > 0) {
      body.productIds = productIds
    }

    if (studyEid) {
      body.studyEid = studyEid
    }

    return this.post<{
      newProducts?: ProductData[]
      skippedCodes?: string[]
      skippedProducts?: SkippedProcedure[]
      createdProductIds?: string[]
    }>(route, body).pipe(
      oMap((response) => ({
        newProducts: (response.data.newProducts ?? []).map((product) => new ProductModel(product)),
        skippedCodes: response.data.skippedCodes ?? [],
        skippedProducts: response.data.skippedProducts ?? [],
        createdProductIds: response.data.createdProductIds ?? [],
      })),
    )
  }

  /**
   * Removes a procedure row from the appointment. The clinic's procedure catalog is left alone. The
   * API refuses the last procedure unless `cancelAppointment` is set, which cancels the appointment
   * with it.
   */
  deleteAppointmentProductCpt(
    appointmentId: string,
    productId: string,
    options: { cancelAppointment?: boolean } = {},
  ): Observable<DeleteProductCptResult> {
    const route = `/appointments/${appointmentId}/products/${productId}/cpt`
    const requestOptions = options.cancelAppointment
      ? { params: new HttpParams().set('cancelAppointment', 'true') }
      : {}
    return this.delete<DeleteProductCptResult>(route, requestOptions).pipe(
      oMap((response) => ({
        removedProductId: response.data.removedProductId,
        appointmentCancelled: response.data.appointmentCancelled === true,
      })),
    )
  }

  patchAppointment(id: string, body?: any): Observable<AppointmentModel> {
    const route = `/appointments/${id}`
    return this.patch<AppointmentData>(route, body).pipe(
      oMap((response) => {
        return new AppointmentModel(response.data)
      }),
    )
  }

  getAppointment(id: string): Observable<AppointmentModel> {
    if (!id) {
      return of(null as unknown as AppointmentModel)
    }

    const route = `/appointments/${id}`
    return this.get<AppointmentData>(route).pipe(
      oMap((response) => new AppointmentModel(response.data)),
    )
  }

  lookupAppointment(
    query: any,
  ): Observable<{
    clinicId: string
    patientId: string
    appointmentId: string
  }> {
    const route = `/appointments/lookup`
    const params = this.convertToParams(query)
    return this.get<any>(route, params).pipe(
      oMap((response) => {
        return response.data
      }),
    )
  }

  mergeAppointments(ids: string[]): Observable<AppointmentModel> {
    const route = `/appointments/merge`
    return this.post<AppointmentData>(route, { ids }).pipe(
      oMap((response) => {
        return new AppointmentModel(response.data)
      }),
    )
  }

  getAppointments(
    filters: GetAppointmentsInput,
  ): Observable<PagedRestResponse<AppointmentModel[]>> {
    if (filters.start) {
      filters.start = moment(filters.start).toISOString()
    }

    if (filters.end) {
      filters.end = moment(filters.end).toISOString()
    }

    const route = `/appointments`
    const params = new HttpParams({ fromObject: filters as any })
    return this.get<AppointmentData[]>(route, params).pipe(
      oMap((response) => {
        const resp: PagedRestResponse<AppointmentModel[]> = {
          ...response,
          data: map(
            response.data,
            (appointmentData) => new AppointmentModel(appointmentData),
          ),
        }
        return resp
      }),
    )
  }

  batchAppointments(ids: string[], action: string, data?: any) {
    const route = `/appointments/batch`
    return this.post<any>(route, {
      ids,
      action,
      data,
    })
  }

  getAppointmentRequests(
    input: GetAppointmentRequestsInput = {},
  ): Observable<PagedRestResponse<AppointmentRequest[]>> {
    const route = '/appointment-requests'
    const params = this.convertToParams(input as any)
    return this.get<AppointmentRequestData[]>(route, params).pipe(
      oMap((response) => {
        const result: PagedRestResponse<AppointmentRequest[]> = {
          ...response,
          data: response.data.map(
            (requestData) => new AppointmentRequest(requestData),
          ),
        }
        return result
      }),
    )
  }

  getAppointmentRequest(
    id: string,
    params: Record<string, string | number | boolean> = {},
  ): Observable<AppointmentRequest> {
    return this.get<AppointmentRequestData>(
      `/appointment-requests/${id}`,
      this.convertToParams(params),
    )
      .pipe(
        oMap((response) => new AppointmentRequest(response.data)),
      )
  }

  deleteAppointmentRequest(id: string): Observable<any> {
    const route = `/appointment-requests/${id}`
    return this.delete<any>(route)
  }

  updateAppointmentRequest(apptRequest: AppointmentRequest, book?: boolean) {
    const route = `/appointment-requests/${apptRequest.id}`
    const params = this.convertToParams({ book })
    return this.put<AppointmentRequestData>(route, apptRequest.toJSON(), { params }).pipe(
      oMap((response) => new AppointmentRequest(response.data)),
    )
  }

  biDemographics(patientId: string, direction: 'in' | 'out' = 'in') {
    const route = `/patients/${patientId}/bi-demographics`
    const body = {
      direction,
      patient: patientId,
    }
    return this.post(route, body)
  }

  charityScreening(patientId: string) {
    const route = `/charity-care/patients/${patientId}/charity-screening`
    return this.post(route, {})
  }

  getPatient(id: string, ignoreCache = false): Observable<Patient> {
    if (!ignoreCache && this.cache.patients[id]) {
      return of(this.cache.patients[id])
    }

    const route = `/patients/${encodeURIComponent(String(id ?? ''))}`
    return this.get<PatientData>(route).pipe(
      oMap((response) => {
        const patient = new Patient(response.data)
        this.cache.patients[id] = patient
        return patient
      }),
    )
  }

  createPatient(patient: Patient) {
    const route = '/patients'
    return this.post<PatientData>(route, patient.toJSON())
  }

  updatePatient(patient: string, body: any): Observable<RestResponse<PatientData>>
  updatePatient(patient: Patient): Observable<RestResponse<PatientData>>
  updatePatient(patient: string | Patient, body?: any): Observable<RestResponse<PatientData>> {
    const id = typeof patient === 'string' ? patient : patient.id
    if (patient instanceof Patient) {
      body = patient.toJSON()
    }

    const route = `/patients/${id}`
    return this.put<PatientData>(route, body)
  }

  deletePatient(patient: Patient) {
    const route = `/patients/${patient.id}`
    return this.delete<any>(route)
  }

  getPatientDocuments(patientId: string, types: string[] = []) {
    const params = this.convertToParams({ types })
    const route = `/patients/${encodeURIComponent(String(patientId ?? ''))}/documents`
    return this.get<any[]>(route, params).pipe(
      oMap(res => orderBy(map(res.data, documentData => new PatientDocument(documentData)), 'createdAt', 'desc')),
    )
  }

  getPatientIntakeForms(patient: string) {
    const route = `/intakes`
    const params = new HttpParams({
      fromObject: {
        patient,
      },
    })
    return this.get<IntakeModel[]>(route, params)
  }

  getIntake(intakeId: string): Observable<IntakeModel> {
    if (this.cache.intakes[intakeId]) {
      return of(this.cache.intakes[intakeId])
    }
    const route = `/intakes/${encodeURIComponent(String(intakeId ?? ''))}`
    return this.get<IntakeData>(route).pipe(
      oMap((response) => {
        const intake = new IntakeModel(response.data)
        this.cache.intakes[intakeId] = intake
        return intake
      }),
    )
  }

  getIntakePdfLink(
    intakeId: string,
    options: {
      file?: string
      html?: boolean
    } = {
      html: false,
    },
  ) {
    const route = `/intakes/${intakeId}/pdf`
    const params = new HttpParams({
      fromObject: {
        file: options.file ?? '',
        html: options.html === true ? 'true' : 'false',
      },
    })
    return this.get<PDFDownloadLink>(route, params)
  }

  getIntakePDFDocuments(intakeId: string) {
    return this.get<ISectionPDFDocumentValue[]>(`/intakes/${intakeId}/pdf-documents`)
  }

  createIntakeCode(appointmentId: string) {
    const route = `/appointments/${appointmentId}/intake-code`
    return this.post<{ code: string }>(route, {})
  }

  createIntake(intake: IntakeModel) {
    const route = `/intakes`
    return this.post<IntakeResponse>(route, intake)
  }

  updateIntake(intake: PatientIntakeFormData) {
    const route = `/intakes/${intake.id}`
    return this.put<IntakeModel>(route, { intakeData: intake }).pipe(
      oMap((response) => new IntakeModel(response.data)),
    )
  }

  getPatientEligibilities(patientId: string): Observable<Eligibility[]> {
    return this.getEligibilities({ patientId }).pipe(
      oMap((resp) => resp.data),
    )
  }

  getEligibilities(query: PagedParams & { patientId?: string }): Observable<PagedRestResponse<Eligibility[]>> {
    const route = `/eligibilities`
    return this.get<EligibilityData[]>(route, this.convertToParams(query as any)).pipe(
      oMap((resp) => {
        return {
          ...resp,
          data: map(resp.data, (eligibility) => new Eligibility(eligibility)),
        }
      }),
    )
  }

  getEligibility(
    eligibilityId: string,
    includeRaw = false,
  ): Observable<Eligibility> {
    if (this.cache.eligibilities[eligibilityId] && !includeRaw) {
      return of(this.cache.eligibilities[eligibilityId])
    }
    const route = `/eligibilities/${eligibilityId}`
    const params = this.convertToParams({ raw: includeRaw })
    return this.get<EligibilityData>(route, params).pipe(
      oMap((resp) => {
        const eligibility = new Eligibility(resp.data)
        this.cache.eligibilities[eligibilityId] = eligibility
        return eligibility
      }),
    )
  }

  updatePatientEligibility(eligibility: Eligibility) {
    const route = `/eligibilities/${eligibility.id}`
    return this.put<EligibilityData>(route, eligibility.toJSON())
  }

  getEligibilityPdfLink(
    eligibilityId: string,
    asHtml?: boolean,
  ) {
    const route = `/eligibilities/${eligibilityId}/pdf`
    const params = new HttpParams({
      fromObject: {
        html: asHtml ? 'true' : 'false',
      },
    })
    return this.get<PDFDownloadLink>(route, params)
  }

  getAppointmentEstimate(appointmentId: string) {
    const route = `/appointments/${encodeURIComponent(String(appointmentId ?? ''))}/estimate`
    return this.get<any>(route).pipe(
      oMap((resp) => resp.data),
    )
  }

  generateAppointmentEstimatePDF(estimateId: string, asHtml?: boolean) {
    const route = `/estimates/${estimateId}/pdf`
    const params = new HttpParams({
      fromObject: {
        html: asHtml ? 'true' : 'false',
      },
    })
    return this.get<PDFDownloadLink>(route, params)
  }

  getModelClaim(query: { query: string, facilityId: string, serviceType: 'I' | 'O', physicianId?: string, estimateId?: string }): Observable<ModelClaimSearchResult> {
    const route = `/estimates/model-claim-search`
    const params = this.convertToParams(query)
    return this.get<ModelClaimSearchResult>(route, params).pipe(
      oMap((response) => response.data),
    )
  }

  getEstimateTemplates(query: { name: string, facilityIds?: string[] }): Observable<EstimateTemplateModel[]> {
    const route = `/estimates/templates/search`
    const params = this.convertToParams(query)
    return this.get<EstimateTemplateModel[]>(route, params).pipe(
      oMap((response) => {
        return map(response.data, (template) => new EstimateTemplateModel(template))
      }),
    )
  }

  getClaims(name: string): Observable<any[]> {
    const route = `/estimates/claims`
    const params = this.convertToParams({ name })
    return this.get<any[]>(route, params).pipe(
      oMap((response) => {
        return map(response.data, (template) => template)
      }),
    )
  }

  getInventoryScheduledVolume<TData = unknown>(
    input: GetInventoryInput = {},
  ) {
    const route = '/appointments/inventory/scheduled-volume'
    const params = this.convertToParams(input as any)
    return this.get<TData>(route, params)
  }

  getInventoryScheduledVolumeDetails(
    input: GetInventoryStatusDetailsInput,
  ) {
    const route = '/appointments/inventory/scheduled-volume/details'
    const params = this.convertToParams(input as any)
    return this.get<any>(route, params).pipe(
      oMap((response) => {
        return {
          ...response,
          data: map(
            response.data,
            (appointmentData) => new AppointmentModel(appointmentData),
          ),
        }
      }),
    )
  }

  updateAppointmentEstimate(appointmentId: string, estimate: AppointmentEstimateSchema) {
    const route = `/appointments/${encodeURIComponent(String(appointmentId ?? ''))}/estimate`
    return this.put<any>(route, estimate)
  }

  exportAppointmentsCsv(startAt: string, endAt: string, options?: { facilityId?: string, categoryId?: string, hideCancelled?: boolean, hidePrepared?: boolean, hidePast?: boolean }): Observable<string> {
    const params = this.convertToParams({
      startAt,
      endAt,
      ...(options?.facilityId && { facilityId: options.facilityId }),
      ...(options?.categoryId && { categoryId: options.categoryId }),
      ...(options?.hideCancelled && { hideCancelled: 'true' }),
      ...(options?.hidePrepared && { hidePrepared: 'true' }),
      ...(options?.hidePast && { hidePast: 'true' }),
    } as any)
    return this.getCSV('/appointments/export', params)
  }

  sendPatientMessage(
    patientId: string,
    params: {
      type: MessageTemplate.TYPE
      method: SendPatientMessageMethod
      appointmentId?: string
      appointmentRequestId?: string
      data?: any
      translatedText?: string
      translate?: boolean
      internal?: boolean
      to?: string
    },
  ) {
    const route = `/patients/${patientId}/messages`
    return this.post<any>(route, params)
  }

  getPatients(
    input: GetPatientsInput = {},
  ): Observable<PagedRestResponse<Patient[]>> {
    if (isString(input.phone)) {
      input.phone = standardizePhone(input.phone)
    }

    const route = '/patients'
    const params = this.convertToParams(input as any)
    return this.get<PatientData[]>(route, params).pipe(
      oMap((response) => {
        const result: PagedRestResponse<Patient[]> = {
          ...response,
          data: response.data.map((patientData) => new Patient(patientData)),
        }
        return result
      }),
    )
  }

  updateClinician(data: any) {
    const route = '/clinicians/profile'
    return this.put<void>(route, data)
  }

  getRoles(): Observable<Role[]> {
    const route = '/roles'
    return this.get<RoleData[]>(route).pipe(
      oMap((response) => {
        const result = response.data.map((roleData) => new Role(roleData))
        return result
      }),
    )
  }

  getClinicianNotifications(input?: {
    status?: Notification.STATUS
    patientId?: string
    facility?: string
  }): Observable<PagedRestResponse<NotificationModel[]>> {
    const route = `/clinicians/notifications`
    if (input?.status == null) {
      delete input?.status
    }
    const params = this.convertToParams(input)
    return this.get<NotificationData[]>(route, params).pipe(
      oMap((response) => {
        const result: PagedRestResponse<NotificationModel[]> = {
          ...response,
          data: response.data.map(
            (notification) => new NotificationModel(notification),
          ),
        }
        return result
      }),
    )
  }

  contactSupport(params: ContactSupportParams) {
    const route = `/clinicians/contact-support`

    return this.post<any>(route, params)
  }

  updateClinicianNotification(notificationId: string, status: string) {
    const route = `/clinicians/notifications/${notificationId}`
    return this.put<void>(route, { status })
  }

  getPatientNotes(patientId: string, appointmentId?: string): Observable<Note[]> {
    const route = `/patients/${patientId}/notes`
    const params = appointmentId ? this.convertToParams({ appointmentId }) : undefined
    return this.get<NoteData[]>(route, params).pipe(
      oMap((response) => {
        return map(response.data, (note) => new Note(note))
      }),
    )
  }

  createNote(note: NoteData) {
    const route = `/patients/${note.patientId}/notes`
    return this.post<NoteData>(route, note)
  }

  processPayment(patientId: string, data: any) {
    const route = `/payments/patients/${patientId}/charges`
    return this.post<PaymentResponse>(route, data)
  }

  refundTransaction(refundDetails: any) {
    const route = `/payments/charges/refund`
    return this.post<RefundResponse>(route, { refundDetails })
  }

  captureTransaction(captureDetails: any) {
    const route = `/payments/charges/capture/create`
    return this.post<CaptureResponse>(route, { captureDetails })
  }

  cancelCaptureTransaction(captureDetails: any) {
    const route = `/payments/charges/capture/cancel`
    return this.post<any>(route, { captureDetails })
  }

  closeDispute(disputeId: string, paymentProvider: string) {
    const route = `/payments/disputes/${disputeId}/close`
    const params = this.convertToParams({ paymentProvider })
    return this.post<any>(route, {}, { params })
  }

  getPatientCreditCards(patientId: string): Observable<Card[]> {
    const route = `/payments/patients/${encodeURIComponent(String(patientId ?? ''))}/payment-methods`
    return this.get<any>(route).pipe(
      oMap((response) => {
        return map(response.data, (card) => new Card(card))
      }),
    )
  }

  getPatientTransactions(
    patientId: string,
    filters: any = {},
  ): Observable<TransactionModel[]> {
    const route = `/transactions`
    filters.patientId = patientId
    const params = new HttpParams({ fromObject: filters })

    return this.get<TransactionData[]>(route, params).pipe(
      oMap((response) => {
        return map(response.data, (transactionData) => {
          return new TransactionModel(transactionData)
        })
      }),
    )
  }

  getTransaction(transactionId: string) {
    const route = `/transactions/${encodeURI(String(transactionId ?? ''))}`
    return this.get<TransactionData>(route).pipe(
      oMap((response) => new TransactionModel(response.data)),
    )
  }

  getTransactionReferences(transactionId: string) {
    const route = `/transactions/${encodeURI(
      String(transactionId ?? ''),
    )}/references`
    return this.get<TransactionData[]>(route).pipe(
      oMap((response) => {
        return map(
          response.data,
          (transactionData) => new TransactionModel(transactionData),
        )
      }),
    )
  }

  getTransactionPDF(transactionId: string) {
    const route = `/transactions/${transactionId}/pdf`
    return this.get<PDFDownloadLink>(route)
  }

  sendTransactionMessage(patientId: string, transactionId: string) {
    const route = `/transactions/${transactionId}/patients/${patientId}/message`
    return this.get<PDFDownloadLink>(route)
  }

  getTransactions(
    input: GetTransactionsInput = {},
  ): Observable<PagedRestResponse<TransactionModel[]>> {
    const route = '/transactions'
    const params = this.convertToParams(input as any)
    return this.get<TransactionData[]>(route, params).pipe(
      oMap((response) => {
        const result: PagedRestResponse<TransactionModel[]> = {
          ...response,
          data: response.data.map(
            (transaction) => new TransactionModel(transaction),
          ),
        }
        return result
      }),
    )
  }

  createUserInvitation(email: string, role: string) {
    const route = `/payments-tilled/users/invitations`
    return this.post<any>(route, { email, role })
  }

  getListUsers(limit?: number | 0, offset?: number | 0) {
    const route = `/payments-tilled/users`
    const params = new HttpParams({
      fromObject: {
        limit: limit || 0,
        offset: offset || 0,
      },
    })
    return this.get<ListUsersResponse>(route, params)
  }

  submitInvoicePayment(invoiceId: string, pay: any) {
    const route = `/invoices/${invoiceId}/pay`
    return this.post<any>(route, pay)
  }

  generateInvoicePDF(invoiceId: string, asHtml?: boolean) {
    const route = `/invoices/${invoiceId}/pdf`
    const params = new HttpParams({
      fromObject: {
        html: asHtml ? 'true' : 'false',
      },
    })
    return this.get<PDFDownloadLink>(route, params)
  }

  getInvoice(invoiceId: string): Observable<InvoiceModel> {
    const route = `/invoices/${invoiceId}`
    return this.get<InvoiceData>(route).pipe(
      oMap((resp) => {
        return new InvoiceModel(resp.data)
      }),
    )
  }

  getInvoices(
    input: GetInvoicesInput = {},
  ): Observable<PagedRestResponse<InvoiceModel[]>> {
    const route = '/invoices'
    const params = this.convertToParams(input as any)
    return this.get<InvoiceData[]>(route, params).pipe(
      oMap((response) => {
        const result: PagedRestResponse<InvoiceModel[]> = {
          ...response,
          data: response.data.map((invoice) => new InvoiceModel(invoice)),
        }
        return result
      }),
    )
  }

  getInvoicesStats() {
    const route = `/invoices/stats`
    return this.get<InvoicesStats>(route)
  }

  sendInvoiceMessage(
    invoiceId: string,
    params: {
      method: string
      what?: string
      facilityId?: string
      to?: string
    },
  ) {
    const route = `/invoices/${invoiceId}/message`
    return this.post<any>(route, params)
  }

  updateInvoice(invoice: InvoiceModel) {
    return this.put<void>(`/invoices/${invoice.id}`, invoice.toJSON())
  }

  createInvoicesFromEstimate(estimateId: string) {
    const route = `/estimates/${estimateId}/invoices`
    return this.post<{ invoices: InvoiceData[], estimateBalanceId?: string, balance?: unknown }>(route, {}).pipe(
      oMap(resp => (resp.data?.invoices ?? []).map(data => new InvoiceModel(data))),
    )
  }

  getEstimateInvoices(estimateId: string) {
    const route = `/estimates/${estimateId}/invoices`
    return this.get<InvoiceData[]>(route).pipe(
      oMap(resp => resp.data.map(data => new InvoiceModel(data))),
    )
  }

  getMCSTraces(estimateId: string) {
    const route = `/estimates/${estimateId}/model-claim-search-traces`
    return this.get<any>(route).pipe(
      oMap(resp => resp.data),
    )
  }

  getCharityCareTrace(patientId: string) {
    const route = `/patients/${patientId}/charity-care-trace`
    return this.get<CharityCareTrace>(route).pipe(
      oMap(resp => resp.data),
    )
  }

  getProductCategories(): Observable<ProductCategory[]> {
    const route = `/products/categories`
    return this.get<ProductCategoryData[]>(route).pipe(
      oMap((response) => response.data.map((c) => new ProductCategory(c))),
    )
  }

  getPatientUploadedFiles(id: string, intakeId?: string) {
    const route = `/patients/${id}/files`
    const params = intakeId
      ? new HttpParams({ fromObject: { intake: intakeId } })
      : undefined
    return this.get<PatientUploadedFile[]>(route, params)
  }

  preparePatientFilesForUpload(
    patient: string,
    files: Array<PatientFileUploadInput>,
  ) {
    const route = `/patients/${patient}/files-prepare`
    return this.post<Array<PatientFileUploadPreparation>>(route, files)
  }

  uploadPatientFileToEMR(
    patient: string,
    files: Array<PatientFileUploadPreparation>,
  ) {
    const route = `/patients/${patient}/files-emr-upload`
    return this.post<any>(route, files)
  }

  uploadPatientFiles(
    patient: string,
    files: Array<PatientFileUploadInput>,
  ) {
    const route = `/patients/${patient}/files`
    return this.post<any>(route, files)
  }

  deletePatientFile(input: {
    patientId: string
    key: string
  }): Observable<any> {
    const route = `/patients/${input.patientId}/files`
    const params = this.convertToParams({ key: input.key })
    return this.delete<void>(route, { params })
  }

  getPatientHistory(id: string, filters?: any): Observable<EventRecord[]> {
    const route = `/patients/${id}/history`
    return this.get<EventRecordData[]>(route, this.convertToParams(filters)).pipe(
      oMap((response) => {
        return map(response.data, (log) => new EventRecord(log))
      }),
    )
  }

  getPatientInsuranceHistory(insuranceId: string, filters?: any): Observable<PatientInsuranceHistoryModel[]> {
    const route = `/insurances/policies/${insuranceId}/history`
    return this.get<PatientInsuranceHistoryData[]>(route, this.convertToParams(filters)).pipe(
      oMap((response) => {
        return map(response.data, (log) => new PatientInsuranceHistoryModel(log))
      }),
    )
  }

  getPatientMessageHistory(id: string) {
    const route = `/patients/${id}/messages/history`
    return this.get<MessageHistoryRecordData[]>(route).pipe(
      oMap((response) => {
        return orderBy(
          map(
            response.data as MessageHistoryRecordData[],
            (record) => new MessageHistoryRecordModel(record),
          ),
          'createdAt',
          'desc',
        )
      }),
    )
  }

  getRecentConversations() {
    const route = `/patients/conversations`
    return this.get<any[]>(route)
  }

  getMessageHistory(input: {
    patientId?: string
    fromDate?: string | Date | moment.Moment
    toDate?: string | Date | moment.Moment
    direction?: string
    lastEvaluatedId?: string
  }): Observable<PagedRestResponse<MessageHistoryRecordModel[]>> {
    const route = `/patients/messages` // TODO: need to remap this route
    const params = this.convertToParams(input)
    return this.get<MessageHistoryRecordData[]>(route, params).pipe(
      oMap((response) => {
        const resp: PagedRestResponse<MessageHistoryRecordModel[]> = {
          ...response,
          data: map(
            response.data,
            (record) => new MessageHistoryRecordModel(record),
          ),
        }
        return resp
      }),
    )
  }

  updateMessageMediaUrls(messageId: string) {

    const route = `/patients/messages/${messageId}/update-media-links`
    return this.get<MessageHistoryRecordModel>(route)
      .pipe(oMap(response => {
        return response.data
      }))
  }

  translate(input: {
    input: string | string[]
    inputLanguage?: string
    outputLanguage: string
    format?: 'text' | 'html'
  }) {
    const route = `/utils/translate`
    return this.post<{ translatedText: string }[]>(route, input)
  }

  putClinic(clinic: Clinic) {
    const route = `/clinic-settings`
    return this.put<void>(route, clinic.toJSON())
  }

  createQuestion(question: IQuestion) {
    const route = `/clinic-settings/questions`
    return this.post<IQuestion>(route, question)
  }

  updateQuestion(question: IQuestion): Observable<void> {
    const route = `/clinic-settings/questions/${question.id}`
    return this.put<void>(route, question).pipe(oMap(() => {}))
  }

  reorderQuestions(questions: IQuestion[]): Observable<void> {
    const route = `/clinic-settings/reorder-intake`
    return this.post<void>(route, { type: 'questions', questions }).pipe(
      oMap(() => {}),
    )
  }

  createConsent(consent: Consent): Observable<Consent> {
    const route = '/clinic-settings/consents'
    return this.post<ConsentData>(route, consent.toJSON()).pipe(
      oMap((response) => new Consent(response.data)),
    )
  }

  updateConsent(consent: Consent): Observable<void> {
    const route = `/clinic-settings/consents/${consent.id}`
    return this.put<void>(route, consent.toJSON()).pipe(oMap(() => {}))
  }

  deleteConsent(consentId: string): Observable<void> {
    const route = `/clinic-settings/consents/${consentId}`
    return this.delete<void>(route).pipe(oMap(() => {}))
  }

  reorderConsents(consents: Consent[]): Observable<void> {
    const route = `/clinic-settings/reorder-intake`
    return this.post<void>(route, { type: 'consents', consents }).pipe(
      oMap(() => {}),
    )
  }

  upsertSection(section: ISection) {
    const route = `/clinic-settings/sections`
    return this.put<ISection>(route, section)
  }

  reorderSections(sections: ISection[]): Observable<void> {
    const route = `/clinic-settings/reorder-intake`
    const data = sections.map((section) => {
      return {
        id: section.id,
        label: section.label,
        order: section.order,
      }
    })
    return this.post<void>(route, { type: 'sections', sections: data }).pipe(
      oMap(() => {}),
    )
  }

  deleteSection(section: ISection): Observable<void> {
    const route = `/clinic-settings/sections/${section.id}`
    return this.delete<void>(route).pipe(oMap(() => {}))
  }

  createProductCategory(category: ProductCategory) {
    const route = `/products/categories`
    return this.post<ProductCategoryData>(route, category)
  }

  updateProductCategory(category: ProductCategory): Observable<void> {
    const route = `/products/categories/${category.id}`
    return this.put<void>(route, category.toJSON()).pipe(oMap(() => {}))
  }

  deleteProductCategory(categoryId: string): Observable<void> {
    const route = `/products/categories/${categoryId}`
    return this.delete<void>(route).pipe(oMap(() => {}))
  }

  createLocation(location: Facility): Observable<Facility> {
    const route = `/clinic-settings/locations`
    return this.post<FacilityData>(route, location.toJSON()).pipe(
      oMap((resp) => new Facility(resp.data)),
    )
  }

  updateLocation(location: Facility): Observable<Facility> {
    const route = `/clinic-settings/locations/${location.id}`
    return this.put<FacilityData>(route, location.toJSON()).pipe(
      oMap((resp) => new Facility(resp.data)),
    )
  }

  deleteLocation(locationId: string): Observable<void> {
    const route = `/clinic-settings/locations/${locationId}`
    return this.delete<void>(route).pipe(oMap(() => {}))
  }

  prepareImport(input: { contentType: string, format: string, dataset: string }): Observable<{ url: string, object: string, contentType: string }> {
    const route = `/clinic-settings/import/prepare`
    const params = this.convertToParams(input)
    return this.get<any>(route, params).pipe(
      oMap((resp) => resp.data),
    )
  }

  createPaymentProviderAccount() {
    return this.post<{ loginUrl: string }>('/clinic-settings/payment/accounts/create', {
      returnUrl: this.getUrl('/settings/payment'),
    })
  }

  createStripeLoginLink() {
    return this.post<{ loginUrl: string }>('/clinic-settings/payment/stripe/login-link', {
      returnUrl: this.getUrl('/settings/payment'),
    })
  }

  createTilledPaymentAccount() {
    const route = '/clinic-settings/payment-tilled/accounts'
    return this.post<any>(route, null).pipe(
      oMap((resp) => {
        return resp.data
      }),
    )
  }

  retrieveTilledPaymentAccount() {
    const route = '/clinic-settings/payment-tilled/accounts'
    return this.get<any>(route).pipe(
      oMap((resp) => {
        return resp.data
      }),
    )
  }

  submitPayment(data: object) {
    const route = '/transactions/pay'
    return this.post<any>(route, { ...data }).pipe(
      oMap((resp) => {
        return resp.data
      }),
    )
  }

  deletePaymentProviderAccount(): Observable<void> {
    const route = `/clinic-settings/payment/accounts`
    return this.delete<void>(route).pipe(oMap((): void => undefined))
  }

  getMessageTemplates(input: {
    includeCampaignTemplates?: boolean
    includeWorkflowTemplates?: boolean
    includeSystemTemplates?: boolean
  }): Observable<MessageTemplateModel[]> {
    const route = `/messages-templates`
    const params = this.convertToParams(input)
    return this.get<MessageTemplateData[]>(route, params).pipe(
      oMap((response) => {
        return response.data.map((template) => new MessageTemplateModel(template))
      }),
    )
  }

  getMessageTemplate(workflowId: string): Observable<MessageTemplateModel> {
    const route = `/messages-templates/${workflowId}`
    return this.get<MessageTemplateData>(route).pipe(
      oMap((response) => {
        return new MessageTemplateModel(response.data)
      }),
    )
  }

  createMessageTemplate(
    messageTemplate: MessageTemplateModel,
  ): Observable<MessageTemplateModel> {
    const route = `/messages-templates`
    return this.post<MessageTemplateData>(route, messageTemplate.toJSON()).pipe(
      oMap((response) => {
        return new MessageTemplateModel(response.data)
      }),
    )
  }

  updateMessageTemplate(model: MessageTemplateModel): Observable<void> {
    const route = `/messages-templates`
    return this.put<void>(route, model).pipe(oMap(() => {}))
  }

  deleteMessageTemplate(messageTemplateId: string): Observable<any> {
    const route = `/messages-templates/${messageTemplateId}`
    return this.delete<void>(route)
  }

  previewMessagesTemplate(options: any): Observable<any> {
    const route = `/messages-templates/preview`
    return this.post<any>(route, options).pipe(
      oMap((response) => {
        return response && response.data
      }),
    )
  }

  getSupportedMessageAttachments(workflowId: string) {
    const route = `/messages-templates/${workflowId}/attachments`
    return this.get<Workflow.Attachment[]>(route).pipe(
      oMap(toData),
    )
  }

  exportQuestions(intakeSection: string, format: 'csv' | 'json' = 'csv') {
    const route = `/clinic-settings/questions/csv`
    const params: any = { format }
    if (intakeSection) {
      params.leg = intakeSection
    }
    const httpParams = new HttpParams({ fromObject: params })
    return this.getCSV(route, httpParams)
  }

  getProcedureInfo(
    procedureCode: string,
    includeModifiers = false,
  ): Observable<Procedure | undefined> {
    const route = `/cms/procedures/${procedureCode}`
    const params = this.convertToParams({
      includeModifiers,
    })
    return this.get<ProcedureData>(route, params).pipe(
      oMap(
        (resp) => {
          return new Procedure(resp.data)
        },
        catchError((err: PatientPalRestError) => {
          if (err.status === NOT_FOUND) {
            return of()
          } else {
            return throwError(() => err)
          }
        }),
      ),
    )
  }

  getProcedureCodeSuggestions(
    procedureNames: string | string[],
  ): Observable<{ query: string; suggestions: ProcedureData[] }[]> {
    const route = `/cms/suggest`
    return this.post<any[]>(route, { q: procedureNames }).pipe(
      oMap((resp) => {
        return resp.data
      }),
    )
  }

  /**
   * The CPT dialog's lookup: the clinic's active procedures by name or code, then the CMS code list,
   * then the NLP model for words. Same endpoint as `getProcedureCodeSuggestions`, opted in by a flag.
   */
  searchCptCodeSuggestions(query: string): Observable<ProcedureSuggestionResults> {
    const route = `/cms/suggest`
    return this.post<{ query: string; suggestions?: ProcedureSuggestion[]; total?: number }[]>(route, {
      q: query,
      clinicProcedures: true,
    }).pipe(
      oMap((resp) => {
        const answer = resp.data[0]
        const suggestions = (answer?.suggestions ?? []).map((suggestion) => ({
          source: suggestion.source,
          code: suggestion.code,
          codes: suggestion.codes?.length ? suggestion.codes : [suggestion.code],
          name: suggestion.name,
          productId: suggestion.productId,
        }))

        // Only the clinic lookup counts its matches; the model path's list is all there is.
        return { suggestions, total: Math.max(answer?.total ?? 0, suggestions.length) }
      }),
    )
  }

  upsertProduct(product: ProductModel, meta: any) {
    if (product.id) {
      return this.updateProduct(product, meta)
    } else {
      return this.createProduct(product, meta)
    }
  }

  createProduct(product: ProductModel, meta: any) {
    const route = `/products`
    return this.post<ProductData>(route, {
      ...product.toJSON(),
      ...meta,
    })
  }

  updateProduct(product: ProductModel, meta: any) {
    const route = `/products/${product.id}`
    return this.put<ProductData>(route, {
      ...product.toJSON(),
      ...meta,
    })
  }

  patchProduct(product: string, data: any) {
    const route = `/products/${product}`
    return this.patch<ProductData>(route, data)
  }

  deleteProduct(productId: string) {
    const route = `/products/${productId}`
    return this.delete<void>(route)
  }

  getProduct(
    productId: string,
    options = { withPrices: false },
  ): Observable<ProductModel> {
    let params = new HttpParams()

    if (options.withPrices) {
      params = params.set('prices', 'true')
    }

    const route = `/products/${productId}`
    return this.get<ProductData>(route, params).pipe(
      oMap((resp) => {
        return new ProductModel(resp.data)
      }),
    )
  }

  createProductPrice(price: Record<string, any>) {
    const route = `/products/${price['productId']}/prices`
    return this.post<ProductData>(route, price)
  }

  updateProductPrice(price: Record<string, any>) {
    const route = `/products/${price['productId']}/prices/${price['id']}`
    return this.put<ProductData>(route, price)
  }

  deleteProductPrice(price: PriceModel) {
    const route = `/products/${price.productId}/prices/${price.id}`
    return this.delete<void>(route)
  }

  searchChargeDescriptionMaster(params: {
    type?: ChargeDescriptionMaster.TYPE,
    ids?: string[],
    cptCodes?: string[],
    /** Single term matched against description, CPT code, procedure code and charge code. */
    search?: string
    facilityIds: string[],
  }) {
    const route = '/charge-description-master/search'

    return this.get<ChargeDescriptionMasterData[]>(
      route,
      this.convertToParams(params as any),
    ).pipe(
      oMap(toData),
    )
  }

  getBillingOverview() {
    const route = `/clinic-billing/overview`
    return this.get<any>(route)
  }

  getBillingEstimate() {
    const route = `/clinic-billing/estimate`
    return this.get<any>(route)
  }

  getBillingStatus() {
    const route = `/clinic-billing/status`
    return this.get<{
      openInvoices: number
      pastDue: boolean
      pastDueDays: number
    }>(route)
  }

  getProceduresEstimates(params: PagedParams) {
    return this.get<ProceduresEstimateItem[]>(
      '/procedures-estimate',
      this.convertToParams(params as any),
    )
  }

  getProceduresEstimate(id: string): Observable<ProceduresEstimate> {
    return this.get<ProceduresEstimateResponse>(`/procedures-estimate/${id}`).pipe(
      oMap(response => ({
        id: response.data.id,
        patient: new Patient(response.data.patient),
        products: response.data.products.map(p => new ProductModel(p)),
        eligibility: response.data.eligibility,
        estimate: response.data.estimate,
      })),
    )
  }

  updatePatientCard(card: any, patientId: string) : Observable<Card> {
    const route = `/payments/patients/${patientId}/payment-methods`
    return this.put<CardData>(route, {
      ...card,
    }).pipe(
      oMap((resp) => new Card(resp.data)),
    )
  }

  deletePatientCard(card: Card, patientId: string) {
    const route = `/payments/patients/${patientId}/payment-methods`
    const params = this.convertToParams({ id: card.id, type: card.provider })
    return this.delete<void>(route, { params })
  }

  getPatientAppointmentHistory(patientId: string, appointmentId?: string): Observable<AppointmentHistoryData[]> {
    const route = `/patients/${patientId}/appointmenthistory`
    const params = appointmentId ? this.convertToParams({ appointmentId }) : undefined
    return this.get<AppointmentHistoryData[]>(route, params).pipe(
      oMap((response) => {
        return map(response.data)
      }),
    )
  }
}
